import React, { useState, useContext, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "../../../index";
import { toast } from "react-toastify";
import ConfirmToast from "../../forms/notification/Confirm";
import ACControlList from "./components/ACControlList";
import FirmwareUpdateBlock from "./components/FirmwareUpdateBlock";
import FirmwareSelector from "./components/FirmwareSelector";
import LogsViewer from "./components/LogsViewer";
import FirmwareOutcomeNotice from "./components/FirmwareOutcomeNotice";
import {
  classifyFirmwareOutcome,
  FirmwareOutcome,
} from "./firmwareStatus";
import { FirmwareType, FirmwareFiles, FirmwareIds, UpdateLog } from "./types";
import "./style.css";
import { logApiError } from "../../../utils/logApiError";

const FirmwareUpdatePanel: React.FC = () => {
  const { adminStore, uploadStore } = useContext(Context);
  const module = adminStore.adminModule;
  const AC = adminStore.AC;

  const [files, setFiles] = useState<FirmwareFiles>({});
  const [uploading, setUploading] = useState<FirmwareType | null>(null);
  const [firmIds, setFirmIds] = useState<FirmwareIds>({
    ESP: null,
    STM: null,
    AC_control: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [updateLogs, setUpdateLogs] = useState<UpdateLog[]>([]);
  // DEV-60: разобранные исходы обновления — показываются и на отказ, и на успех.
  const [outcomes, setOutcomes] = useState<FirmwareOutcome[]>([]);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null,
  );
  // const [updatingType, setUpdatingType] = useState<'ESP' | 'STM' | 'AC_control' | null>(null);

  const firmwareTypes: readonly FirmwareType[] = ["ESP", "STM", "AC_control"];
  const updatingType = adminStore.updatingType as FirmwareType | null;

  useEffect(() => {
    adminStore.fetchFirmWare();
  }, []);

  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);

  useEffect(() => {
    if (
      adminStore.updatingType &&
      adminStore.updatingModuleId &&
      adminStore.lastUpdateStartedAt
    ) {
      setUpdateLogs(adminStore.updateLogs || []); // подстраховка
      startPolling(adminStore.updatingModuleId, adminStore.lastUpdateStartedAt);
    }

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const handleUpload = async (type: FirmwareType) => {
    const file = files[type];
    if (!file) {
      setError("Сначала выберите файл");
      return;
    }
    // Устанавливаем состояние загрузки для конкретного типа
    setUploading(type);

    // Вызываем метод из uploadStore
    const result = await uploadStore.uploadFirmware(file, type);

    // Обрабатываем результат
    if (result?.conflict) {
      // Если файл существует, показываем диалог подтверждения
      toast(
        <ConfirmToast
          message={`Файл "${result.filename}" уже существует. Заменить его?`}
          onConfirm={() => replaceFirmware(type, file)} // Вызываем replaceFirmware с файлом
          onCancel={() => {
            toast.info("Загрузка отменена");
            // Сбрасываем состояние загрузки, если пользователь отменил
            setUploading(null);
          }}
          type={"del"}
          title={"заменить"}
        />,
        {
          autoClose: false,
          closeOnClick: false,
        },
      );
      // ВАЖНО: Не сбрасываем setUploading(null) здесь, потому что процесс может продолжиться
      // через replaceFirmware или onCancel
      return; // Выходим, чтобы не выполнить код ниже
    }

    if (result?.success) {
      // Если успешно загружено
      setFiles((prev) => ({ ...prev, [type]: undefined })); // Очищаем выбранный файл
      adminStore.fetchFirmWare(); // Обновляем список прошивок в сторе
      toast.success(`Прошивка ${type} успешно загружена`);
    } else {
      // Если была ошибка
      setError(
        `Не удалось загрузить ${type}: ${result?.message || "Неизвестная ошибка"}`,
      );
    }

    // В любом случае (успех, ошибка, но НЕ конфликт), сбрасываем состояние загрузки
    setUploading(null);
  };

  // --- ЗАМЕНИТЬ ЭТУ ФУНКЦИЮ ---
  const replaceFirmware = async (
    type: FirmwareType,
    file: File,
  ) => {
    // Вызываем метод из uploadStore с флагом replace = true
    const result = await uploadStore.uploadFirmware(
      file,
      type,
      true /* replace */,
    );

    if (result?.success) {
      // Если успешно заменено
      setFiles((prev) => ({ ...prev, [type]: undefined })); // Очищаем выбранный файл
      adminStore.fetchFirmWare(); // Обновляем список прошивок в сторе
      toast.success(`Прошивка ${type} успешно заменена`);
    } else {
      // Если была ошибка при замене
      toast.error(result?.message || "Не удалось заменить прошивку");
    }

    // Обязательно сбрасываем состояние загрузки после завершения
    setUploading(null);
  };

  // ... остальной код компонента (startUpdate, startPolling, stopPolling, return ...)

  const startUpdate = async (
    firmId: number,
    moduleId: string,
    type: FirmwareType,
  ) => {
    if (!firmId || firmId <= 0) {
      alert("Выберите прошивку");
      return;
    }

    adminStore.setUpdatingState(
      type,
      moduleId,
      new Date().toISOString(),
    );

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    try {
      await adminStore.updateFirmWare(firmId, moduleId, type);
      setUpdateLogs([]);
      startPolling(moduleId, adminStore.lastUpdateStartedAt!);
    } catch (e) {
      adminStore.clearUpdatingState();
      alert("Ошибка при запуске обновления");
    }
  };

  const startPolling = (moduleId: string, startedAt: string) => {
    const interval = setInterval(async () => {
      try {
        // `pollFirmLogs` отдаёт ответ как `unknown`; журнал обновления — это
        // UpdateLog[], и без явного типа разбор ниже не типизируется.
        const logs = (await adminStore.pollFirmLogs(
          moduleId,
          startedAt,
        )) as UpdateLog[];
        setUpdateLogs(logs);

        // DEV-60: разбираем не отдельные значения, а исход целиком — отказ
        // ESP, STM и станции одинаково. Останавливаемся на любом итоговом
        // исходе, а не только на transferred / «finish to all ACC»: иначе
        // новый отказ станции не был бы замечен вовсе.
        const classified = logs
          .map((log) => classifyFirmwareOutcome(log))
          .filter((outcome): outcome is FirmwareOutcome => outcome !== null);

        if (classified.length > 0) {
          setOutcomes(classified);
        }

        const failures = classified.filter(
          (outcome) => outcome.outcome === "failure",
        );
        const finalOutcome = classified.find((outcome) => outcome.final);

        if (finalOutcome) {
          clearInterval(interval);
          setPollingInterval(null);

          if (adminStore.updatingType) {
            adminStore.setUpdateStatus(
              adminStore.updatingType,
              failures.length > 0 ? "failed" : "complete",
            );
          }

          adminStore.clearUpdatingState();
        }
      } catch (err) {
        logApiError("Ошибка при получении логов", err);
      }
    }, 3000);
    setPollingInterval(interval);
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    adminStore.clearUpdatingState();
    toast.info("Отслеживание обновлений остановлено");
  };

  return (
    <div className="FW-panel">
      <div className="FW-management">
        <FirmwareSelector onStopPolling={stopPolling} />

        {module && (
          <div>
            {firmwareTypes.map((type) => {
              const currentVersion = module[type] || "";
              const availableFirmwares = adminStore.firmWares.filter(
                (fw) => fw.type === type,
              );

              return (
                <FirmwareUpdateBlock
                  key={type}
                  type={type}
                  currentVersion={currentVersion}
                  firmwares={availableFirmwares}
                  selectedId={firmIds[type]}
                  onIdChange={(id) =>
                    setFirmIds((prev) => ({ ...prev, [type]: id }))
                  }
                  file={files[type]}
                  uploading={uploading === type}
                  onFileChange={(file) => {
                    setFiles((prev) => ({ ...prev, [type]: file }));
                    // Сбрасываем uploading для этого типа
                    if (uploading === type) {
                      setUploading(null);
                    }
                  }}
                  onUpload={() => handleUpload(type)}
                  onStartUpdate={() =>
                    startUpdate(firmIds[type]!, module.charge_id, type)
                  }
                  disabledUpdate={!firmIds[type]}
                  isUpdating={updatingType === type}
                  updateStatus={
                    adminStore.updatingType === type
                      ? undefined
                      : adminStore.updateStatus[type]
                  }
                />
              );
            })}
            <ACControlList items={Array.isArray(AC) ? AC : []} />

            {error && (
              <div
                className="FW-error"
                style={{ color: "red", marginTop: "10px" }}
              >
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      <LogsViewer logs={updateLogs} />

      {/* DEV-60: каждый отказ — с номером станции и причиной; итог успеха —
          одной строкой. Отказ станции, отказ STM и отказ ESP показываются
          одинаково. */}
      {outcomes
        .filter((outcome) => outcome.outcome === "failure" || outcome.final)
        .map((outcome, index) => (
          <FirmwareOutcomeNotice
            key={`${index}-${outcome.component}-${outcome.reason}-${outcome.stationNumber}`}
            outcome={outcome}
          />
        ))}

      {/* 2. Список прошивок (по центру) */}
      <div className="FW-firmwares">
        <h3>Доступные прошивки</h3>
        <div
          className="FW-firmwares-list"
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            fontSize: "13px",
          }}
        >
          {adminStore.firmWares.length === 0 ? (
            <p style={{ color: "#666", fontStyle: "italic" }}>
              Нет доступных прошивок
            </p>
          ) : (
            adminStore.firmWares.map((fw) => (
              <div
                key={fw.id}
                className="FW-firmwareItem"
                style={{
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  marginBottom: "4px",
                  lineHeight: "1.4",
                }}
              >
                <div>
                  <strong>{fw.type}</strong>: {fw.version}
                  <br />
                </div>
                <button
                  onClick={() => adminStore.deleteFirmWare(fw.id)}
                  style={{
                    width: "30px",
                    height: "30px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    lineHeight: "1",
                    borderRadius: "4px",
                    backgroundColor: "#ff5454",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  X
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(FirmwareUpdatePanel);
